import { Controller } from 'stimulus'
import { attachDynamicEvent } from '~/common/helpers/dom'

/*
 * Give any button `data-trigger-contracts=true`
 * Once that button is clicked, we will find the contracts controller element within the form
 * And show all the contracts.
 * Once all the contracts are accepted your form submit/other action will continue
 */

const TRIGGER_ATTR_NAME = 'data-trigger-contracts'

export default class extends Controller {
  static targets = ['contract']

  show({ detail: { trigger, callback } }) {
    // Store the trigger that showed contracts. We will use it later to trigger a click on it again once we are done.
    this.trigger = trigger

    // Store the callback to use when contracts are completed/cancelled
    this.callback = callback

    this.showNextUnsignedContract()
  }

  contractSigned() {
    this.showNextUnsignedContract()
  }

  contractCancelled() {
    // Call the callback with false to indicate contracts were not completed
    if (this.callback) this.callback(false)
  }

  showNextUnsignedContract() {
    const nextUnsignedContract = this.unsignedContracts[0]

    if (!nextUnsignedContract) {
      this.allContractsCompleted()
      return
    }

    this.dispatch('contract:show', {
      prefix: false,
      target: nextUnsignedContract,
    })
  }

  allContractsCompleted() {
    // As for today, we show all the contracts (this component)
    // after clicking a button (e.g. form submit) where we set the data-trigger-contracts attribute,
    // OR by firing a contracts:show event, on which we should pass a callback
    // to be used when all contracts are completed.

    if (this.trigger) {
      // Remove the trigger attribute so we don't get stuck in an infinite loop
      // And then trigger a click on the trigger element to continue the form submission/whatever.
      this.trigger.removeAttribute(TRIGGER_ATTR_NAME)
      this.trigger.click()
    } else if (this.callback) {
      // Call the callback with true to indicate all contracts were signed
      this.callback(true)
    } else {
      this.element.closest('form').requestSubmit()
    }
  }

  get unsignedContracts() {
    return this.contractTargets.filter((contract) => !contract.dataset.signed)
  }
}

function updateSignerNamesFromForm(form, contractsEl) {
  const firstNames = form.querySelector('input[name$="[first_names]"]')
  const lastName = form.querySelector('input[name$="[last_name]"]')
  if (!firstNames && !lastName) return

  const name = [firstNames?.value, lastName?.value]
    .filter(Boolean)
    .join(' ')
    .trim()
  if (!name) return

  contractsEl.querySelectorAll('[data-signer-name]').forEach((el) => {
    el.textContent = name
  })
}

attachDynamicEvent(
  `[${TRIGGER_ATTR_NAME}]`,
  'click',
  function (e) {
    const form = this.closest('form')
    const contractsController = form.querySelector(
      '[data-controller=contracts--contracts]'
    )

    if (!contractsController) return

    e.preventDefault()
    e.stopPropagation()

    updateSignerNamesFromForm(form, contractsController)

    const detail = {
      trigger: this.closest('[data-trigger-contracts]'),
    }

    contractsController.dispatchEvent(
      new CustomEvent('contracts:show', { detail })
    )
  },
  { capture: true }
)
